import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { TwoFactorPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import AuthLayout from "../layout/AuthLayout.jsx";
import { InlineMessage, errorText } from "../ui/Feedback.jsx";

/**
 * 6-digit code step. Used by signup (verifying the address), login (the second
 * factor), and from the settings screen (the secret-reveal flow), so the challenge
 * id — and the purpose it belongs to — arrive through router state.
 *
 * The purpose travels with every call: the same endpoint completes all three
 * flows, and the server namespaces codes by purpose, so a verify request without
 * one is a guess the server has to resolve.
 */
export default function TwoFactorScreen() {
  const api = useApiClient();
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const state = location.state ?? {};
  const challengeId = state.challengeId;

  if (!challengeId) return <Navigate to="/login" replace />;

  async function handleVerify(code) {
    setError(null);
    setNotice(null);
    try {
      const data = await api.auth.verifyTwoFactor({
        challenge_id: challengeId,
        code,
        purpose: state.purpose,
      });
      adoptSession(data);
      navigate(state.nextPath || "/", { replace: true });
    } catch (err) {
      if (err?.status === 410) {
        setError("That code expired or was already used — request a new one.");
      } else if (err?.status === 400) {
        setError("That code isn't right. Check the digits and try again.");
      } else {
        setError(errorText(err, "Could not verify that code."));
      }
    }
  }

  async function handleResend() {
    setError(null);
    setNotice(null);
    try {
      await api.auth.resendTwoFactor({ challenge_id: challengeId, purpose: state.purpose });
      setNotice(state.purpose === "verify_email" ? "We sent a new code by e-mail." : "We sent a new code.");
    } catch (err) {
      if (err?.status === 429) {
        setError(
          err.isNotConfigured
            ? err.message
            : "Too many codes requested — wait a moment before asking for another.",
        );
      } else if (err?.isNotConfigured) {
        setError(err.message);
      } else {
        setError(errorText(err, "Could not resend the code."));
      }
    }
  }

  return (
    <AuthLayout>
      {state.notice ? <InlineMessage tone="warning">{state.notice}</InlineMessage> : null}
      {notice ? <InlineMessage tone="success">{notice}</InlineMessage> : null}
      {error ? <InlineMessage tone="danger">{error}</InlineMessage> : null}
      <TwoFactorPage
        destination={state.destination || "your registered contact"}
        onVerify={handleVerify}
        onResend={handleResend}
      />
    </AuthLayout>
  );
}
